import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminLogo from '../../assets/admin-logo.png';

const AdminTopNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = () => {
    // Only clear admin tokens from admin components
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminRole');
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard' },
    { path: '/admin/user-management', label: 'Users' },
    { path: '/admin/card-management', label: 'Cards' },
    { path: '/admin/broadcast-card-management', label: 'Broadcast Manage' },
    { path: '/admin/daily-challenge-cards', label: 'Daily Cards' },
    { path: '/admin/streamer-challenges', label: 'Challenges' },
    { path: '/admin/overlay', label: 'Card Display' },
    { path: '/admin/payment-management', label: 'Payments' },
    { path: '/admin/settings', label: 'Settings' },
    { path: '/admin/profile', label: 'Profile' },
  ];

  return (
    <nav className="bg-gray-900 border-b border-gray-800 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Logo and Navigation */}
        <div className="flex items-center space-x-8">
          {/* Logo */}
          <Link to="/admin/dashboard" className="flex items-center space-x-2">
            {/* Logo with safe fallback */}
            <div className="relative w-8 h-8">
              <img
                src={(typeof window !== 'undefined' && window._ADMIN_LOGO) || adminLogo}
                alt="Admin"
                className="w-8 h-8 object-contain rounded"
                onError={(e) => {
                  // Hide broken image and show fallback badge
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.parentElement?.querySelector('[data-fallback-logo]');
                  if (fallback) fallback.classList.remove('hidden');
                }}
              />
              {/* Fallback badge */}
              <div
                data-fallback-logo
                className="hidden absolute inset-0 flex items-center justify-center rounded bg-gradient-to-br from-red-600 to-red-400 text-white text-xs font-bold"
                aria-label="Admin"
                title="Admin"
              >
                AD
              </div>
            </div>
            <span className="text-red-400 font-bold text-xl">ADMIN DASHBOARD</span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(item.path)
                    ? 'bg-red-500 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Right side - Settings, Admin, Logout */}
        <div className="flex items-center space-x-4">
          {/* Admin Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center space-x-2 p-2 rounded-lg transition-all duration-200 ${isActive('/admin/profile')
                  ? 'bg-red-500 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-medium">CREZAR</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50">
                <div className="py-1">
                  <Link
                    to="/admin/profile"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/admin/settings"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300 transition-colors duration-200"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden mt-4 pt-4 border-t border-gray-800">
        <div className="grid grid-cols-2 gap-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-3 py-2 rounded-lg text-sm font-medium text-center transition-all duration-200 ${isActive(item.path)
                  ? 'bg-red-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default AdminTopNavbar;
